import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { ConsultationStatusDefinition } from '../types/Consultation';
import styles from './ConsultationRecordShell.module.css';

export type ConsultationContextChip = {
  label: string;
  value: React.ReactNode;
};

export type ConsultationContextHeaderProps = {
  title: string;
  subtitle?: React.ReactNode;
  statusLabel?: string;
  statusIsCompleted?: boolean;
  aiStatusLine?: React.ReactNode;
  chips: ConsultationContextChip[];
  onCopyLink?: () => void;
  calendarLink?: {
    to: string;
    state?: Record<string, unknown>;
  };
  extraActions?: React.ReactNode;
};

export const ConsultationContextHeader: React.FC<ConsultationContextHeaderProps> = ({
  title,
  subtitle,
  statusLabel,
  statusIsCompleted = false,
  aiStatusLine,
  chips,
  onCopyLink,
  calendarLink,
  extraActions,
}) => {
  const { t } = useTranslation(['consultations', 'common']);

  return (
    <section className={`${styles.panel} ${styles.contextHeader}`}>
      <div className={styles.titleRow}>
        <div>
          <h1 className={styles.title}>{title}</h1>
          {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          {aiStatusLine ? <p className={styles.subtitle}>{aiStatusLine}</p> : null}
        </div>
        {statusLabel ? (
          <span className={`${styles.statusBadge} ${statusIsCompleted ? styles.statusBadgeCompleted : ''}`}>
            {statusLabel}
          </span>
        ) : null}
      </div>

      <div className={styles.contextChips}>
        {chips.map((chip) => (
          <div key={chip.label} className={styles.contextChip}>
            <span className={styles.chipLabel}>{chip.label}</span>
            <span className={styles.chipValue}>{chip.value}</span>
          </div>
        ))}
      </div>

      <div className={styles.headerActions}>
        {onCopyLink ? (
          <button className="btn btn-secondary" type="button" onClick={() => void onCopyLink()}>
            {t('copyLink', { ns: 'common', defaultValue: 'Copy link' })}
          </button>
        ) : null}
        {calendarLink ? (
          <Link className="btn btn-secondary" to={calendarLink.to} state={calendarLink.state}>
            {t('openInCalendar', { ns: 'calendar', defaultValue: 'Open in calendar' })}
          </Link>
        ) : null}
        {extraActions}
      </div>
    </section>
  );
};

export const resolveConsultationStatusDefinition = (
  status: string,
  definitions: ConsultationStatusDefinition[],
): ConsultationStatusDefinition | undefined => {
  return definitions.find((item) => item.name === status);
};
